/**
 * Chip — compact state marker for list rows and headers (severity, type,
 * status, "seen N×", persona). 12px sans, sentence case, tone-tinted.
 *
 * Tones pair a faint mark tint (fill) with the AAA ink of the same hue
 * (text), so every tone meets 7:1 in both themes. Meaning is carried by
 * the word, never by colour alone.
 */

import type * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const chipVariants = cva(
  "inline-flex h-5 shrink-0 items-center gap-1 whitespace-nowrap rounded-sm px-1.5 font-sans text-xs font-medium tabular-nums [&>svg]:size-3 [&>svg]:shrink-0",
  {
    variants: {
      tone: {
        neutral: "bg-muted text-foreground",
        muted: "bg-muted text-muted-foreground",
        critical:
          "bg-mark-critical/10 text-severity-critical dark:bg-mark-critical/20 border border-mark-critical/20 dark:border-mark-critical/30",
        important:
          "bg-mark-important/10 text-severity-important dark:bg-mark-important/20 border border-mark-important/20 dark:border-mark-important/30",
        helps:
          "bg-mark-helps/10 text-finding-helps dark:bg-mark-helps/20 border border-mark-helps/20 dark:border-mark-helps/30",
        primary:
          "bg-primary/10 text-primary-ink border border-primary/25 dark:bg-primary/15",
        outline: "border border-border/80 text-muted-foreground bg-muted/20",
      },
    },
    defaultVariants: { tone: "neutral" },
  },
);

export type ChipTone = NonNullable<VariantProps<typeof chipVariants>["tone"]>;

export function Chip({
  className,
  tone,
  ...props
}: React.ComponentProps<"span"> & VariantProps<typeof chipVariants>) {
  return (
    <span
      data-slot="chip"
      className={cn(chipVariants({ tone }), className)}
      {...props}
    />
  );
}
